import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getMembershipLabel } from './membershipTypes';

function Kursplan() {
  const { user } = useAuth();
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(getWeekStart(new Date()));
  const [selectedClass, setSelectedClass] = useState(null);
  const [bookingInProgress, setBookingInProgress] = useState(false);
  const [message, setMessage] = useState(null);
  const [quota, setQuota] = useState(null); // Kontingent-Info
  const [filter, setFilter] = useState('all'); // 'all', 'courses', 'events'

  // Get Monday of the current week
  function getWeekStart(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  // Get all days of the week
  function getWeekDays(weekStart) {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      days.push(day);
    }
    return days;
  }

  // Format date to ISO string (YYYY-MM-DD) - timezone safe
  function toISODate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Navigate weeks
  const prevWeek = () => {
    const newWeek = new Date(currentWeek);
    newWeek.setDate(newWeek.getDate() - 7);
    setCurrentWeek(newWeek);
  };

  const nextWeek = () => {
    const newWeek = new Date(currentWeek);
    newWeek.setDate(newWeek.getDate() + 7);
    setCurrentWeek(newWeek);
  };

  const goToToday = () => {
    setCurrentWeek(getWeekStart(new Date()));
  };

  // Check if user is logged in
  const isLoggedIn = !!user?.token;

  // Fetch schedule and quota for current week
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const weekDays = getWeekDays(currentWeek);
      const startDate = toISODate(weekDays[0]);
      const endDate = toISODate(weekDays[6]);

      try {
        if (isLoggedIn) {
          // Logged in: Fetch full schedule with participants
          const [scheduleRes, quotaRes] = await Promise.all([
            fetch(`/api/member/schedule?start_date=${startDate}&end_date=${endDate}&filter=${filter}`, {
              headers: { 'Authorization': `Bearer ${user.token}` }
            }),
            fetch(`/api/member/quota?week_start=${startDate}`, {
              headers: { 'Authorization': `Bearer ${user.token}` }
            })
          ]);

          const scheduleData = await scheduleRes.json();
          const quotaData = await quotaRes.json();

          if (scheduleData.data) {
            setSchedule(scheduleData.data);
          }
          if (quotaData.data) {
            setQuota(quotaData.data);
          }
        } else {
          // Not logged in: Fetch public schedule (no participants)
          const scheduleRes = await fetch(`/api/public/kursplan?start_date=${startDate}&end_date=${endDate}&filter=${filter}`);
          const scheduleData = await scheduleRes.json();

          if (scheduleData.data) {
            setSchedule(scheduleData.data);
          }
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      }
      setLoading(false);
    };

    fetchData();
  }, [currentWeek, user, filter, isLoggedIn]);

  // Refresh quota after booking
  const refreshQuota = async () => {
    if (!user?.token) return;
    const weekDays = getWeekDays(currentWeek);
    const startDate = toISODate(weekDays[0]);
    try {
      const res = await fetch(`/api/member/quota?week_start=${startDate}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      const data = await res.json();
      if (data.data) setQuota(data.data);
    } catch (err) {
      console.error('Error refreshing quota:', err);
    }
  };

  // Group schedule by day
  const scheduleByDay = {};
  const weekDays = getWeekDays(currentWeek);
  weekDays.forEach(day => {
    scheduleByDay[toISODate(day)] = [];
  });
  schedule.forEach(item => {
    if (scheduleByDay[item.date]) {
      scheduleByDay[item.date].push(item);
    }
  });

  // Book/Unbook a specific date (not entire course)
  const handleBooking = async (slotId, date, isBooked) => {
    setBookingInProgress(true);
    setMessage(null);
    const token = user?.token;

    try {
      if (isBooked) {
        // Unbook this specific date
        const res = await fetch(`/api/member/book-course/${slotId}/${date}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.message === 'success') {
          setMessage({ type: 'success', text: 'Du wurdest erfolgreich ausgebucht.' });
          // Refresh schedule and quota
          const weekDays = getWeekDays(currentWeek);
          const startDate = toISODate(weekDays[0]);
          const endDate = toISODate(weekDays[6]);
          const schedRes = await fetch(`/api/member/schedule?start_date=${startDate}&end_date=${endDate}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const schedData = await schedRes.json();
          if (schedData.data) setSchedule(schedData.data);
          await refreshQuota();
          setSelectedClass(null);
        } else {
          setMessage({ type: 'error', text: data.error || 'Ein Fehler ist aufgetreten.' });
        }
      } else {
        // Book this specific date
        const res = await fetch('/api/member/book-course', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ slot_id: slotId, date })
        });
        const data = await res.json();
        if (data.message === 'success') {
          setMessage({ type: 'success', text: 'Du wurdest erfolgreich eingebucht!' });
          // Refresh schedule and quota
          const weekDays = getWeekDays(currentWeek);
          const startDate = toISODate(weekDays[0]);
          const endDate = toISODate(weekDays[6]);
          const schedRes = await fetch(`/api/member/schedule?start_date=${startDate}&end_date=${endDate}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const schedData = await schedRes.json();
          if (schedData.data) setSchedule(schedData.data);
          await refreshQuota();
          setSelectedClass(null);
        } else {
          setMessage({ type: 'error', text: data.error || 'Ein Fehler ist aufgetreten.' });
        }
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Verbindungsfehler. Bitte versuche es erneut.' });
    }
    setBookingInProgress(false);
  };

  // Book/Unbook an open time
  const handleOpenTimeBooking = async (openTimeId, date, isBooked) => {
    setBookingInProgress(true);
    setMessage(null);
    const token = user?.token;

    try {
      if (isBooked) {
        // Unbook
        const res = await fetch(`/api/open-times/${openTimeId}/book`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ date })
        });
        const data = await res.json();
        if (data.message === 'success') {
          setMessage({ type: 'success', text: 'Du wurdest erfolgreich abgemeldet.' });
          // Refresh schedule
          const weekDays = getWeekDays(currentWeek);
          const startDate = toISODate(weekDays[0]);
          const endDate = toISODate(weekDays[6]);
          const schedRes = await fetch(`/api/member/schedule?start_date=${startDate}&end_date=${endDate}&filter=${filter}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const schedData = await schedRes.json();
          if (schedData.data) setSchedule(schedData.data);
          setSelectedClass(null);
        } else {
          setMessage({ type: 'error', text: data.error || 'Ein Fehler ist aufgetreten.' });
        }
      } else {
        // Book
        const res = await fetch(`/api/open-times/${openTimeId}/book`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ date })
        });
        const data = await res.json();
        if (data.message === 'success') {
          setMessage({ type: 'success', text: 'Du wurdest erfolgreich angemeldet!' });
          // Refresh schedule
          const weekDays = getWeekDays(currentWeek);
          const startDate = toISODate(weekDays[0]);
          const endDate = toISODate(weekDays[6]);
          const schedRes = await fetch(`/api/member/schedule?start_date=${startDate}&end_date=${endDate}&filter=${filter}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const schedData = await schedRes.json();
          if (schedData.data) setSchedule(schedData.data);
          setSelectedClass(null);
        } else {
          setMessage({ type: 'error', text: data.error || 'Ein Fehler ist aufgetreten.' });
        }
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Verbindungsfehler. Bitte versuche es erneut.' });
    }
    setBookingInProgress(false);
  };

  const formatTime = (timeStr) => timeStr?.substring(0, 5) || '';

  const dayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  const today = toISODate(new Date());

  // Check if quota is exhausted
  const quotaExhausted = quota && quota.remaining <= 0 && quota.weekly_limit < 99;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Kursplan</h1>
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={prevWeek}>
            &larr; Vorherige
          </button>
          <button className="btn btn-outline-primary btn-sm" onClick={goToToday}>
            Heute
          </button>
          <button className="btn btn-outline-secondary btn-sm" onClick={nextWeek}>
            Naechste &rarr;
          </button>
        </div>
      </div>

      {/* Hinweis fuer nicht-angemeldete Besucher */}
      {!isLoggedIn && (
        <div className="alert alert-info d-flex justify-content-between align-items-center mb-3">
          <div>
            <strong>Unser Kursangebot</strong>
            <span className="ms-2">
              Du siehst hier unseren aktuellen Kursplan. Melde dich an oder schnuppere erst mal rein!
            </span>
          </div>
          <a href="/schnuppern" className="btn btn-primary btn-sm">
            Zum Schnuppern
          </a>
        </div>
      )}

      {/* Kontingent-Anzeige (nur fuer angemeldete Benutzer) */}
      {isLoggedIn && quota && quota.weekly_limit < 99 && (
        <div className={`alert ${quotaExhausted ? 'alert-warning' : 'alert-info'} d-flex justify-content-between align-items-center mb-3`}>
          <div>
            <strong>{getMembershipLabel(quota.membership_type)}</strong>
            <span className="ms-2">
              {quota.used}/{quota.weekly_limit} Kurse diese Woche gebucht
            </span>
          </div>
          <div>
            {quotaExhausted ? (
              <span className="badge bg-warning text-dark">Kontingent erreicht</span>
            ) : (
              <span className="badge bg-success">Noch {quota.remaining} frei</span>
            )}
          </div>
        </div>
      )}

      {/* 10er-Karte Credits Anzeige */}
      {isLoggedIn && quota && quota.membership_type === 'zehnerkarte' && (
        <div className={`alert ${quota.credits <= 0 ? 'alert-danger' : quota.credits <= 2 ? 'alert-warning' : 'alert-info'} d-flex justify-content-between align-items-center mb-3`}>
          <div>
            <strong>10er-Karte</strong>
            <span className="ms-2">
              {quota.credits !== null ? quota.credits : 0} von 10 Punkten übrig
            </span>
          </div>
          <div>
            {quota.credits <= 0 ? (
              <span className="badge bg-danger">Keine Punkte mehr</span>
            ) : quota.credits <= 2 ? (
              <span className="badge bg-warning text-dark">Bald aufgebraucht</span>
            ) : (
              <span className="badge bg-success">{quota.credits} Punkte</span>
            )}
          </div>
        </div>
      )}

      {/* Week Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="text-muted mb-0">
          {weekDays[0].toLocaleDateString('de-DE', { day: '2-digit', month: 'long' })} - {weekDays[6].toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}
        </h5>

        {/* Filter-Buttons */}
        <div className="btn-group" role="group">
          <button
            type="button"
            className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFilter('all')}
          >
            Alles
          </button>
          <button
            type="button"
            className={`btn btn-sm ${filter === 'courses' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFilter('courses')}
          >
            Kurse
          </button>
          <button
            type="button"
            className={`btn btn-sm ${filter === 'events' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFilter('events')}
          >
            Events
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'} alert-dismissible`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Laden...</span>
          </div>
        </div>
      ) : (
        <>
          {/* Calendar Grid */}
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead>
                <tr>
                  {weekDays.map((day, idx) => (
                    <th
                      key={idx}
                      className={`text-center ${toISODate(day) === today ? 'bg-warning bg-opacity-25' : ''}`}
                      style={{ width: '14.28%', minWidth: '120px' }}
                    >
                      <div className="fw-bold">{dayNames[idx]}</div>
                      <div className="small">{day.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {weekDays.map((day, idx) => {
                    const dateStr = toISODate(day);
                    const dayClasses = scheduleByDay[dateStr] || [];

                    return (
                      <td
                        key={idx}
                        className={`align-top p-2 ${dateStr === today ? 'bg-warning bg-opacity-10' : ''}`}
                        style={{ minHeight: '150px', verticalAlign: 'top' }}
                      >
                        {dayClasses.length === 0 ? (
                          <div className="text-muted small text-center py-3">-</div>
                        ) : (
                          dayClasses.map((cls, clsIdx) => {
                            const isOpenTime = cls.item_type === 'open_time';
                            const itemColor = cls.course_color || (cls.item_type === 'ki_lab' ? '#9c27b0' : cls.item_type === 'feriencamp' ? '#ff9800' : '#324047');

                            return (
                            <div
                              key={clsIdx}
                              className="card mb-2 cursor-pointer"
                              style={{
                                cursor: 'pointer',
                                borderLeft: `4px ${isOpenTime ? 'dashed' : 'solid'} ${itemColor}`,
                                border: isOpenTime ? `2px dashed ${itemColor}` : undefined,
                                borderLeftWidth: isOpenTime ? '4px' : undefined,
                                backgroundColor: cls.is_booked ? 'rgba(248, 16, 177, 0.1)' : isOpenTime ? `${itemColor}10` : 'white'
                              }}
                              onClick={() => setSelectedClass(cls)}
                            >
                              <div className="card-body p-2">
                                <div className="d-flex justify-content-between align-items-start mb-1">
                                  <div className="small fw-bold" style={{ color: itemColor }}>
                                    {cls.course_name}
                                  </div>
                                  {cls.item_type === 'ki_lab' && (
                                    <span className="badge" style={{ backgroundColor: '#9c27b0', fontSize: '0.65em' }}>KI-Lab</span>
                                  )}
                                  {cls.item_type === 'feriencamp' && (
                                    <span className="badge" style={{ backgroundColor: '#ff9800', fontSize: '0.65em' }}>Feriencamp</span>
                                  )}
                                  {isOpenTime && (
                                    <span className="badge" style={{ backgroundColor: itemColor, fontSize: '0.65em' }}>Offen</span>
                                  )}
                                </div>
                                <div className="small text-muted">{formatTime(cls.time)} Uhr</div>
                                <div className="small">
                                  {isLoggedIn ? (
                                    // Angemeldete Benutzer: Volle Info anzeigen
                                    cls.bookable !== false || cls.item_type === 'feriencamp' ? (
                                      <>
                                        {cls.is_booked && <span className="badge bg-primary">{isOpenTime ? 'Angemeldet' : 'Gebucht'}</span>}
                                        {isOpenTime && cls.requires_digital_pilot && !cls.has_access && (
                                          <span className="badge bg-warning text-dark ms-1">Nur Digital-Pilot</span>
                                        )}
                                      </>
                                    ) : (
                                      <span className="badge bg-secondary">Info-Event</span>
                                    )
                                  ) : (
                                    // Nicht angemeldete Besucher: Nur Kurstyp anzeigen
                                    <span className="badge bg-light text-dark">
                                      {cls.duration ? `${cls.duration}h` : 'Kurs'}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );})
                        )}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div className="d-flex flex-wrap gap-4 mt-3 small text-muted">
            <div><span className="badge bg-primary">Gebucht</span> Du bist eingebucht</div>
          </div>
        </>
      )}

      {/* Detail Modal */}
      {selectedClass && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setSelectedClass(null)}>
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header" style={{
                borderColor: selectedClass.course_color || (selectedClass.item_type === 'ki_lab' ? '#9c27b0' : selectedClass.item_type === 'feriencamp' ? '#ff9800' : '#324047'),
                borderStyle: selectedClass.item_type === 'open_time' ? 'dashed' : 'solid',
                borderWidth: selectedClass.item_type === 'open_time' ? '2px' : undefined
              }}>
                <h5 className="modal-title d-flex align-items-center gap-2" style={{ color: selectedClass.course_color || (selectedClass.item_type === 'ki_lab' ? '#9c27b0' : selectedClass.item_type === 'feriencamp' ? '#ff9800' : '#324047') }}>
                  {selectedClass.course_name}
                  {selectedClass.item_type === 'ki_lab' && (
                    <span className="badge" style={{ backgroundColor: '#9c27b0', fontSize: '0.6em' }}>KI-Lab</span>
                  )}
                  {selectedClass.item_type === 'feriencamp' && (
                    <span className="badge" style={{ backgroundColor: '#ff9800', fontSize: '0.6em' }}>Feriencamp</span>
                  )}
                  {selectedClass.item_type === 'open_time' && (
                    <span className="badge" style={{ backgroundColor: selectedClass.course_color || '#00bcd4', fontSize: '0.6em' }}>Offene Zeit</span>
                  )}
                </h5>
                <button type="button" className="btn-close" onClick={() => setSelectedClass(null)}></button>
              </div>
              <div className="modal-body">
                {/* Date & Time */}
                <div className="mb-3">
                  <strong>Termin:</strong><br />
                  {new Date(selectedClass.date).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}<br />
                  {formatTime(selectedClass.time)} Uhr ({selectedClass.duration}h)
                </div>

                {/* Description */}
                {(selectedClass.course_description || selectedClass.module_description) && (
                  <div className="mb-3">
                    <p className="text-muted">{selectedClass.course_description || selectedClass.module_description}</p>
                  </div>
                )}

                {/* Fuer nicht-angemeldete: Schnupper-Hinweis */}
                {!isLoggedIn ? (
                  <div className="alert alert-info mb-0">
                    <strong>Interesse an diesem Kurs?</strong><br />
                    <p className="mb-2">Melde dich an oder starte einen Schnupper-Pass!</p>
                    <div className="d-flex gap-2">
                      <a href="/schnuppern" className="btn btn-primary btn-sm">Schnuppern</a>
                      <a href="/login" className="btn btn-outline-secondary btn-sm">Anmelden</a>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Participants - fuer buchbare Kurse und Feriencamps (nur fuer angemeldete) */}
                    {(selectedClass.bookable !== false || selectedClass.item_type === 'feriencamp') && (
                      <div className="mb-3">
                        <strong>Teilnehmer:</strong><br />
                        {selectedClass.participants && selectedClass.participants.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1 mt-2">
                            {selectedClass.participants.map((name, idx) => (
                              <span key={idx} className="badge bg-light text-dark">{name}</span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted">Noch keine Teilnehmer</span>
                        )}
                      </div>
                    )}

                    {/* Booking Status (nur fuer angemeldete) */}
                    {selectedClass.bookable !== false ? (
                      selectedClass.is_booked ? (
                        <div className="alert alert-success mb-0">
                          <strong>Du bist fuer diesen Termin eingebucht!</strong>
                        </div>
                      ) : selectedClass.booked_count >= selectedClass.max_attendees ? (
                        <div className="alert alert-warning mb-0">
                          Leider keine freien Plaetze mehr.
                        </div>
                      ) : quotaExhausted ? (
                        <div className="alert alert-warning mb-0">
                          Dein Wochen-Kontingent ist erschoepft ({quota.weekly_limit} Kurs{quota.weekly_limit > 1 ? 'e' : ''} pro Woche mit {getMembershipLabel(quota.membership_type)}).
                        </div>
                      ) : (
                        quota && quota.weekly_limit < 99 && (
                          <div className="alert alert-info mb-0">
                            Dein Kontingent: {quota.remaining} von {quota.weekly_limit}
                          </div>
                        )
                      )
                    ) : selectedClass.item_type === 'feriencamp' ? (
                  selectedClass.is_booked ? (
                    <div className="alert alert-success mb-0">
                      <strong>Du bist fuer dieses Feriencamp angemeldet!</strong>
                    </div>
                  ) : selectedClass.booked_count >= selectedClass.max_attendees ? (
                    <div className="alert alert-warning mb-0">
                      Dieses Feriencamp ist leider ausgebucht.
                    </div>
                  ) : (
                    <div className="alert alert-info mb-0">
                      Noch {selectedClass.max_attendees - selectedClass.booked_count} Plaetze frei!
                      <br /><small className="text-muted">Anmeldung ueber die Feriencamp-Seite.</small>
                    </div>
                  )
                ) : selectedClass.item_type === 'open_time' ? (
                  <>
                    {/* Participants for open time */}
                    <div className="mb-3">
                      <strong>Angemeldet:</strong><br />
                      {selectedClass.participants && selectedClass.participants.length > 0 ? (
                        <div className="d-flex flex-wrap gap-1 mt-2">
                          {selectedClass.participants.map((name, idx) => (
                            <span key={idx} className="badge bg-light text-dark">{name}</span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">Noch keine Anmeldungen</span>
                      )}
                    </div>

                    {/* Status alert */}
                    {selectedClass.requires_digital_pilot && !selectedClass.has_access ? (
                      <div className="alert alert-warning mb-0">
                        <strong>Nur fuer Digital-Pilot Mitglieder</strong><br />
                        <small>Diese offene Zeit ist nur fuer Digital-Pilot und Schnupper-Pass Mitglieder verfuegbar.</small>
                      </div>
                    ) : selectedClass.is_booked ? (
                      <div className="alert alert-success mb-0">
                        <strong>Du bist angemeldet!</strong>
                      </div>
                    ) : selectedClass.booked_count >= selectedClass.max_attendees ? (
                      <div className="alert alert-warning mb-0">
                        Leider keine freien Plaetze mehr.
                      </div>
                    ) : (
                      <div className="alert mb-0" style={{ backgroundColor: `${selectedClass.course_color || '#00bcd4'}20`, borderColor: selectedClass.course_color || '#00bcd4' }}>
                        <strong>Offene Zeit</strong>
                        {selectedClass.requires_digital_pilot && (
                          <span className="badge bg-info ms-2">Nur Digital-Pilot</span>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="alert alert-secondary mb-0">
                    <strong>Info-Event</strong> - Dieses Event ist nicht ueber den Kursplan buchbar.
                    {selectedClass.item_type === 'ki_lab' && ' KI-Lab Events finden regelmaessig statt.'}
                  </div>
                )}
                  </>
                )}
              </div>
              <div className="modal-footer">
                {/* Course booking buttons - nur fuer angemeldete Benutzer */}
                {isLoggedIn && selectedClass.bookable !== false && selectedClass.item_type !== 'open_time' && (
                  selectedClass.is_booked ? (
                    <button
                      className="btn btn-outline-danger"
                      onClick={() => handleBooking(selectedClass.slot_id, selectedClass.date, true)}
                      disabled={bookingInProgress}
                    >
                      {bookingInProgress ? 'Wird bearbeitet...' : 'Ausbuchen'}
                    </button>
                  ) : selectedClass.booked_count < selectedClass.max_attendees && !quotaExhausted && (
                    <button
                      className="btn btn-primary"
                      onClick={() => handleBooking(selectedClass.slot_id, selectedClass.date, false)}
                      disabled={bookingInProgress}
                    >
                      {bookingInProgress ? 'Wird bearbeitet...' : 'Jetzt einbuchen'}
                    </button>
                  )
                )}
                {/* Open time booking buttons - nur fuer angemeldete Benutzer */}
                {isLoggedIn && selectedClass.item_type === 'open_time' && selectedClass.has_access && (
                  selectedClass.is_booked ? (
                    <button
                      className="btn btn-outline-danger"
                      onClick={() => handleOpenTimeBooking(selectedClass.open_time_id, selectedClass.date, true)}
                      disabled={bookingInProgress}
                    >
                      {bookingInProgress ? 'Wird bearbeitet...' : 'Abmelden'}
                    </button>
                  ) : selectedClass.booked_count < selectedClass.max_attendees && (
                    <button
                      className="btn btn-primary"
                      onClick={() => handleOpenTimeBooking(selectedClass.open_time_id, selectedClass.date, false)}
                      disabled={bookingInProgress}
                    >
                      {bookingInProgress ? 'Wird bearbeitet...' : 'Jetzt anmelden'}
                    </button>
                  )
                )}
                <button className="btn btn-secondary" onClick={() => setSelectedClass(null)}>
                  Schliessen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Kursplan;
